import { SVAutocomplete, SVButton, SVSelect } from '@/components/core';
import { FORM_FIELDS } from '@/constants/formFields';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  BankApplicationAccountType,
  BankApplicationFormValues,
} from '@/types/bankApplications';
import { SelectOption } from '@/types/inputs';
import { Card, Typography, Grid, Radio, FormLabel } from '@mui/material';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

type BankAccountApplicationBasicInfoProps = {
  customerList: SelectOption[];
  companiesList: SelectOption[];
  teammatesList: SelectOption[];
  businessList: SelectOption[];
  setOpenBusinessModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenOwnerModal: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function BankAccountApplicationBasicInfo({
  customerList,
  companiesList,
  teammatesList,
  businessList,
  setOpenBusinessModal,
  setOpenOwnerModal,
}: BankAccountApplicationBasicInfoProps) {
  const { isStaff } = useAuthUser();

  const formMethods = useFormContext<BankApplicationFormValues>();
  const { watch, setValue } = formMethods;

  const [
    applicationId,
    accountType,
    companyId,
    advisorId,
    jointOwnerIds,
    primaryOwner,
  ] = watch([
    'applicationId',
    'accountType',
    'companyId',
    'advisorId',
    'jointOwnerIds',
    'primaryOwner',
  ]);
  const isEdit = Boolean(applicationId);
  const isIndividualAccount =
    accountType === BankApplicationAccountType.INDIVIDUAL;
  const isBusinessAccount = accountType === BankApplicationAccountType.BUSINESS;

  function clearIds() {
    setValue('individualOwnerId', '');
    setValue('jointOwnerIds', []);
    setValue('businessId', '');
  }

  useEffect(() => {
    clearIds();
  }, [accountType, advisorId]);

  useEffect(() => {
    setValue('advisorId', '');
    clearIds();
  }, [companyId]);

  useEffect(() => {
    if (isBusinessAccount) {
      return;
    }

    if (jointOwnerIds.length > 1 && !primaryOwner) {
      setValue('primaryOwner', jointOwnerIds[0]);
    }
    if (jointOwnerIds.length === 1) {
      setValue('primaryOwner', '');
    }
  }, [jointOwnerIds]);

  const jointOwners = customerList.filter(item =>
    jointOwnerIds.includes(item.value.toString()),
  );

  return (
    <>
      <Card>
        <Typography variant="h5" mb={2}>
          Basic Info
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <SVSelect
              name="accountType"
              label="Account Type"
              options={[
                {
                  label: 'Individual',
                  value: BankApplicationAccountType.INDIVIDUAL,
                },
                {
                  label: 'Joint',
                  value: BankApplicationAccountType.JOINT,
                },
                {
                  label: 'Business',
                  value: BankApplicationAccountType.BUSINESS,
                },
              ]}
              rules={{ required: true }}
              disabled={isEdit}
            />
          </Grid>
          {isStaff && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SVAutocomplete
                name="companyId"
                options={companiesList}
                {...FORM_FIELDS.COMPANY}
                disabled={isEdit}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <SVAutocomplete
              label="Advisor"
              name="advisorId"
              options={teammatesList}
              rules={{ required: true }}
              disabled={!companyId || !isStaff}
            />
          </Grid>
          {advisorId && (
            <Grid size={{ xs: 12, md: 6 }}>
              {isBusinessAccount ? (
                <>
                  <SVAutocomplete
                    label="Business"
                    name="businessId"
                    options={businessList}
                    rules={{ required: true }}
                    disabled={!advisorId}
                  />
                  <SVButton
                    variant="outlined"
                    onClick={() => setOpenBusinessModal(true)}>
                    Add Business
                  </SVButton>
                </>
              ) : (
                <>
                  <SVAutocomplete
                    label="Owners"
                    name={
                      isIndividualAccount
                        ? 'individualOwnerId'
                        : 'jointOwnerIds'
                    }
                    options={customerList}
                    rules={{ required: true }}
                    disabled={!advisorId}
                    multiple={!isIndividualAccount}
                  />
                  <SVButton
                    variant="outlined"
                    onClick={() => setOpenOwnerModal(true)}>
                    Add Owner
                  </SVButton>
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Card>
      {jointOwnerIds.length > 1 ? (
        <Card>
          <Typography variant="h5" mb={2}>
            Primary Owner
          </Typography>
          {jointOwners.map(owner => (
            <Grid key={owner.value} container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <Typography>{owner.label}</Typography>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Radio
                  checked={owner.value === primaryOwner}
                  onChange={() => {
                    setValue('primaryOwner', owner.value.toString());
                  }}
                  value={owner.value === primaryOwner}
                />
                <FormLabel>Is Primary Owner</FormLabel>
              </Grid>
            </Grid>
          ))}
        </Card>
      ) : null}
    </>
  );
}
